<template>
    <div class="tw-px-4">
        <div class="tw-grid tw-grid-cols-12 tw-gap-4">
            <!-- Left Sidebar - Hidden on mobile -->
            <div v-if="loading" class="tw-col-span-12 md:tw-col-span-3 tw-hidden md:tw-block">
                <div class="tw-flex tw-flex-col tw-gap-4">
                    <!-- "You may also like" Skeleton -->
                    <div
                        class="tw-w-full tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-animate-pulse">
                        <!-- Title Skeleton -->
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mb-2 tw-animate-pulse"></div>
                        <!-- Description Skeleton -->
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-4 tw-animate-pulse"></div>
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                        </div>
                    </div>

                    <!-- "Your connections" Skeleton -->
                    <div
                        class="tw-w-full tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-animate-pulse">
                        <!-- Title Skeleton -->
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-2/3 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Left Sidebar - Hidden on mobile -->
            <div v-if="!loading" class="tw-col-span-12 md:tw-col-span-3 tw-hidden md:tw-block">
                <div class="tw-flex tw-flex-col tw-gap-4">
                    <div
                        class="tw-w-full tw-min-h-[300px]x tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{
                            $t('common.you-may-also-like') }}</h1>
                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ $t('common.you-may-also-like-description') }}
                        </p>
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <!-- Recommendation Cards -->
                            <router-link 
                                v-for="recommendation in recommendations" 
                                :key="recommendation.id"
                                :to="{ name: 'shared.community-member', params: { id: recommendation.id } }"
                                class="tw-w-full tw-h-[70px] tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 hover:tw-border-primary-300 tw-p-3 tw-flex tw-items-center tw-gap-3 tw-transition-all tw-duration-200 hover:tw-shadow-sm tw-cursor-pointer"
                            >
                                <!-- Profile Image with Role Ring -->
                                <div :class="getRecommendationRoleColors(recommendation.role).ring"
                                     class="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-200 tw-border-2 tw-ring-2 tw-border-solid tw-flex-shrink-0 tw-overflow-hidden tw-relative">
                                    <img 
                                        :src="recommendation.profile_image" 
                                        :alt="recommendation.name"
                                        class="tw-w-full tw-h-full tw-object-cover tw-rounded-full"
                                    >
                                </div>
                                
                                <!-- Member Info -->
                                <div class="tw-flex-1 tw-min-w-0">
                                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-truncate">
                                        {{ recommendation.name }}
                                    </h3>
                                    <p class="tw-text-xs tw-text-gray-500 tw-truncate">
                                        {{ recommendation.company?.name || $t('common.no-company') }}
                                    </p>
                                    <span :class="getRecommendationRoleColors(recommendation.role).text"
                                          class="tw-text-xs tw-font-medium">
                                        {{ recommendation.badge }}
                                    </span>
                                </div>
                            </router-link>
                            
                            <!-- Empty State - Show placeholders if no recommendations -->
                            <div v-if="recommendations.length === 0" class="tw-flex tw-flex-col tw-gap-2">
                                <div class="tw-w-full tw-h-[70px] tw-bg-gray-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                                    <span class="tw-text-xs tw-text-gray-400">{{ $t('common.no-recommendations') }}</span>
                                </div>
                            </div>
                            
                        </div>
                    </div>

                    <div
                        class="tw-w-full tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.your-connections')
                            }}</h1>
                        <div class="tw-flex tw-flex-col tw-gap-2">
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-100 tw-rounded-lg"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-100 tw-rounded-lg"></div>
                            <div class="tw-w-full tw-h-[70px] tw-bg-gray-100 tw-rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Content - Full width on mobile -->
            <!-- Loading Skeleton -->
            <div v-if="loading" class="tw-col-span-12 md:tw-col-span-6">
                <div
                    class="tw-w-full tw-pb-8 tw-min-h-[200px] md:tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-overflow-hidden tw-animate-pulse">
                    <div class="tw-flex tw-items-center tw-justify-center tw-p-5 tw-flex-col">
                        <!-- Avatar Skeleton -->
                        <div class="tw-w-[150px] tw-h-[150px] tw-rounded-full tw-bg-gray-200 tw-mx-auto tw-mt-2 tw-border-4 tw-border-solid tw-border-gray-300 tw-ring tw-ring-gray-200 tw-animate-pulse">
                        </div>

                        <!-- Name Skeleton -->
                        <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-mt-4 tw-mb-2 tw-animate-pulse"></div>
                        
                        <!-- Company Skeleton -->
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-4 tw-animate-pulse"></div>

                        <!-- Connect Button Skeleton -->
                        <div class="tw-mt-0">
                            <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-[170px] tw-animate-pulse"></div>
                        </div>
                    </div>

                    <!-- About Section Skeleton -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-20 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mb-4 tw-animate-pulse"></div>
                    </div>

                    <!-- Multi Section Skeleton -->
                    <div class="tw-grid md:tw-grid-cols-2 tw-grid-cols-1">
                        <!-- Country Section Skeleton -->
                        <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-20 tw-mb-2 tw-animate-pulse"></div>
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-4 tw-animate-pulse"></div>
                        </div>

                        <!-- Social Media Section Skeleton -->
                        <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-28 tw-mb-2 tw-animate-pulse"></div>
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-4 tw-animate-pulse"></div>
                        </div>
                    </div>

                    <!-- Website Section Skeleton -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-20 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-4 tw-animate-pulse"></div>
                    </div>

                    <!-- Contact Section Skeleton -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-20 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-4 tw-animate-pulse"></div>
                    </div>

                    <!-- Shared Contacts Section Skeleton -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-3 tw-mb-4">
                            <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-lg tw-bg-gray-100 tw-animate-pulse">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-flex-1"></div>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-lg tw-bg-gray-100 tw-animate-pulse">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-flex-1"></div>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-rounded-lg tw-bg-gray-100 tw-animate-pulse">
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200"></div>
                                <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-flex-1"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Events Section Skeleton -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16 tw-mb-2 tw-animate-pulse"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-8 tw-mb-4 tw-animate-pulse"></div>
                    </div>
                </div>
            </div>

            <!-- Main Content - Full width on mobile -->
            <div v-if="!loading" class="tw-col-span-12 md:tw-col-span-6">
                <div
                    class="tw-w-full tw-pb-8 tw-min-h-[200px] md:tw-min-h-[300px] tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-overflow-hidden">
                    <div class="tw-flex tw-items-center tw-justify-center tw-p-5 tw-flex-col">
                        <div :class="roleColors.ring"
                            class="tw-w-[150px] tw-h-[150px] tw-rounded-full tw-bg-gray-200 tw-mx-auto tw-mt-2 tw-border-4 tw-ring-4 tw-border-solid group-hover:tw-border-primary-400x tw-duration-200 tw-relative">
                            <img :src="member.profile_image" alt="member avatar"
                                class="tw-w-full tw-h-full tw-rounded-full tw-object-contain">

                            <!-- Curved Role Badge -->
                            <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center -tw-rotate-45">
                                <svg class="tw-w-full tw-h-full tw-absolute -tw-bottom-[20px]" viewBox="0 0 100 100">
                                    <defs>
                                        <path id="bottom-arc" d="M 15,75 A 50,50 0 0,0 85,75" />
                                    </defs>
                                    <text :class="[roleColors.stroke, roleColors.text]"
                                        class="tw-text-[16px] tw-font-black" text-anchor="middle" stroke-width="0.8"
                                        letter-spacing="1">
                                        <textPath href="#bottom-arc" startOffset="50%">
                                            {{ member.badge }}
                                        </textPath>
                                    </text>
                                </svg>
                            </div>
                        </div>

                        <h1 class="tw-text-2xl tw-font-bold tw-text-primary-800 tw-mb-2 tw-mt-4">{{ member.name }}</h1>
                        <p v-if="member.company?.name" class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ member.company?.name }}</p>
                        <p v-else class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ $t('common.no-company') }}</p>

                        <div class="tw-mt-0">
                            <!-- Pending Connection - Sent by me -->
                            <div v-if="member.connection?.status === 'pending' && member.connection?.is_sent_by_me" class="tw-flex tw-gap-2">
                                <a-button 
                                    intent="transparent"
                                    left-icon="solar:close-circle-bold"
                                    @click="showCancelPopup = true"
                                >
                                    {{ $t('buttons.cancel') }}
                                </a-button>

                                <a-button 
                                    full 
                                    intent="warning"
                                    left-icon="solar:clock-circle-bold"
                                    class="tw-min-w-[170px] tw-cursor-default tw-pointer-events-none"
                                >
                                    {{ $t('common.pending') }}
                                </a-button>
                            </div>

                            <!-- Pending Connection - Received by me -->
                            <div v-else-if="member.connection?.status === 'pending' && !member.connection?.is_sent_by_me" class="tw-flex tw-gap-2">
                                <a-button 
                                    intent="transparent"
                                    left-icon="solar:close-circle-bold"
                                    @click="showDeclinePopup = true"
                                >
                                    {{ $t('buttons.decline') }}
                                </a-button>

                                <a-button 
                                    full 
                                    intent="success"
                                    left-icon="solar:check-circle-bold"
                                    class="tw-min-w-[170px]"
                                    @click="handleAcceptRequest"
                                >
                                    {{ $t('buttons.accept') }}
                                </a-button>
                            </div>

                            <!-- Accepted Connection -->
                            <a-button 
                                v-else-if="member.connection?.status === 'accepted'"
                                full 
                                intent="primary"
                                left-icon="solar:chat-round-dots-bold-duotone"
                                class="tw-min-w-[170px]"
                                :loading="chatLoading"
                                @click="handleStartChat"
                            >
                                {{ $t('buttons.start-chat') }}
                            </a-button>

                            <!-- Connect Button -->
                            <a-button 
                                v-else-if="member.connection?.can_connect !== false"
                                full 
                                intent="gradient" 
                                left-icon="solar:user-plus-bold-duotone" 
                                class="tw-min-w-[170px]"
                                @click="showConnectionPopup = true"
                            >
                                {{ $t('buttons.connect') }}
                            </a-button>
                        </div>
                    </div>

                    <!-- About Section -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.about') }}</h1>
                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4 tw-whitespace-pre-line">{{ member?.profile?.about || $t('common.no-about-section') }}</p>
                    </div>

                    <!-- Multi Section -->
                    <div class="tw-grid md:tw-grid-cols-2 tw-grid-cols-1">

                        <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.country') }}</h1>
                            <p class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ member?.profile?.country ?? '-' }}</p>
                        </div>

                        <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.social-media') }}</h1>
                            <div v-if="socialMediaLinks.length > 0" class="tw-flex tw-flex-wrap tw-gap-3 tw-mb-4">
                                <a 
                                    v-for="social in socialMediaLinks" 
                                    :key="social.platform"
                                    :href="social.url" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    class="tw-inline-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-primary-100 hover:tw-text-primary-600 tw-transition-colors tw-duration-200"
                                    :title="social.platform"
                                >
                                    <icon :icon="social.icon" class="tw-text-xl" />
                                </a>
                            </div>
                            <p v-else class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ $t('common.no-social-media-available') }}</p>
                        </div>
                    </div>

                    <!-- Multi Section -->
                    <div class="tw-grid md:tw-grid-cols-2 tw-grid-cols-1">
                        <!-- Website Section -->
                        <div v-if="websiteUrl" class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2 tw-flex tw-items-center">
                                {{ $t('common.website') }}
                            </h1>
                            <a v-if="websiteUrl" 
                               :href="websiteUrl" 
                               target="_blank" 
                               rel="noopener noreferrer" 
                               class="tw-text-sm tw-text-primary-600 hover:tw-text-primary-800 tw-underline tw-mb-4 tw-break-all tw-block tw-w-fit">
                                {{ websiteUrl }}
                            </a>
                            <p v-else class="tw-text-sm tw-text-gray-500 tw-mb-4">-</p>
                        </div>
    
                        <!-- Website Section -->
                        <div v-if="websiteUrl" class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                            <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2 tw-flex tw-items-center">
                                {{ $t('fields.email') }}
                            </h1>
                            <a v-if="member.contact_email" 
                               :href="`mailto:${member.contact_email}`" 
                               target="_blank" 
                               rel="noopener noreferrer" 
                               class="tw-text-sm tw-text-primary-600 hover:tw-text-primary-800 tw-underline tw-mb-4 tw-break-all tw-block tw-w-fit">
                                {{ websiteUrl }}
                            </a>
                            <p v-else class="tw-text-sm tw-text-gray-500 tw-mb-4">-</p>
                        </div>
                    </div>


                    <!-- Contact Section -->
                    <div v-if="false" class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.contact') }}</h1>
                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4">-</p>
                    </div>

                    <!-- Shared Contacts Section -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">
                            {{ $t('common.shared-contacts') }}
                            <span v-if="mutualConnectionsCount > 0" class="tw-text-sm tw-font-normal tw-text-gray-500 tw-ml-1">
                                ({{ mutualConnectionsCount }})
                            </span>
                        </h1>
                        <div v-if="mutualConnections.length > 0" class="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-3 tw-mb-4">
                            <router-link 
                                :to="{ name: 'shared.community-member', params: { id: connection.id } }"
                                v-for="connection in mutualConnections.slice(0, 6)" 
                                :key="connection.id"
                                class="tw-flex tw-items-center tw-border tw-border-solid tw-border-gray-200 tw-gap-2 tw-p-2 tw-rounded-lg tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors tw-duration-200 tw-cursor-pointer"
                            >
                                <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-200 tw-border tw-border-solid tw-border-gray-300 tw-flex-shrink-0 tw-overflow-hidden">
                                    <img 
                                        :src="connection.profile_image" 
                                        :alt="connection.name"
                                        class="tw-w-full tw-h-full tw-object-cover"
                                    >
                                </div>
                                <span class="tw-text-xs tw-text-gray-700 tw-font-medium tw-truncate">
                                    {{ connection.name }}
                                </span>
                            </router-link>
                            <div 
                                v-if="mutualConnectionsCount > 6" 
                                class="tw-flex tw-items-center tw-justify-center tw-p-2 tw-rounded-lg tw-bg-primary-50 tw-border tw-border-primary-200"
                            >
                                <span class="tw-text-xs tw-text-primary-600 tw-font-medium">
                                    +{{ mutualConnectionsCount - 6 }} more
                                </span>
                            </div>
                        </div>
                        <p v-else class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ $t('common.no-link') }}</p>
                    </div>

                    <!-- Events Section -->
                    <div class="tw-px-4 tw-border-t tw-border-gray-200 tw-border-solid tw-py-2">
                        <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('common.events') }}</h1>
                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4">-</p>
                    </div>

                </div>
            </div>

            <!-- Right Sidebar - Hidden on mobile -->
            <!-- Loading Skeleton for Right Sidebar -->
            <div v-if="loading" class="tw-col-span-12 md:tw-col-span-3 tw-hidden md:tw-block">
                <div class="tw-w-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-animate-pulse">
                    <!-- Title Skeleton -->
                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-2/3 tw-mb-2 tw-animate-pulse"></div>
                    <!-- Description Skeleton -->
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full tw-mb-2 tw-animate-pulse"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-mb-4 tw-animate-pulse"></div>
                    <!-- Button Skeleton -->
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-full tw-animate-pulse"></div>
                </div>
            </div>

            <!-- Right Sidebar - Hidden on mobile -->
            <div v-if="!loading" class="tw-col-span-12 md:tw-col-span-3 tw-hidden md:tw-block">
                <div class="tw-w-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200">
                    <h1 class="tw-text-lg tw-font-bold tw-text-primary-800 tw-mb-2">{{ $t('buttons.book-meeting') }}
                    </h1>
                    <p class="tw-text-sm tw-text-gray-500 tw-mb-4">{{ $t('common.book-meeting-description') }}</p>
                    <a-button full intent="gradient" left-icon="solar:calendar-bold-duotone" @click="showBookMeetingPopup = true">
                        {{ $t('buttons.book-meeting') }}
                    </a-button>
                </div>
            </div>
        </div>

        <!-- Connection Request Popup -->
        <ConnectionRequestPopup
            :visible="showConnectionPopup"
            :target-user="member"
            @close="showConnectionPopup = false"
            @sent="handleConnectionSent"
        />

        <!-- Book Meeting Popup -->
        <BookMeetingPopup
            :visible="showBookMeetingPopup"
            :target-user="member"
            @close="showBookMeetingPopup = false"
            @meeting-created="handleMeetingCreated"
        />

        <!-- Cancel Confirmation Popup -->
        <a-popup :visible="showCancelPopup" @cancel="showCancelPopup = false">
            <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[400px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-relative tw-bg-gradient-to-br tw-from-rose-50 tw-to-rose-100/50 tw-px-6 tw-pt-8 tw-pb-4">
                    <div class="tw-absolute tw-top-4 tw-right-4">
                        <button @click="showCancelPopup = false" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                            <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                        </button>
                    </div>
                    
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-p-2 tw-bg-white/80 tw-rounded-xl">
                            <icon icon="solar:close-circle-bold" class="tw-text-2xl tw-text-rose-500" />
                        </div>
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
                            {{ $t('common.cancel-connection-request') }}
                        </h3>
                    </div>
                </div>

                <!-- Content -->
                <div class="tw-p-6">
                    <p class="tw-text-gray-600">
                        {{ $t('messages.confirm-cancel-connection-request') }}
                    </p>
                </div>

                <!-- Footer -->
                <div class="tw-px-6 tw-pb-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <a-button 
                        intent="white" 
                        @click="showCancelPopup = false"
                    >
                        {{ $t('buttons.no-cancel') }}
                    </a-button>
                    <a-button 
                        intent="danger" 
                        @click="confirmCancelRequest"
                    >
                        {{ $t('buttons.yes-proceed') }}
                    </a-button>
                </div>
            </div>
        </a-popup>

        <!-- Decline Confirmation Popup -->
        <a-popup :visible="showDeclinePopup" @cancel="showDeclinePopup = false">
            <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[400px] tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-relative tw-bg-gradient-to-br tw-from-rose-50 tw-to-rose-100/50 tw-px-6 tw-pt-8 tw-pb-4">
                    <div class="tw-absolute tw-top-4 tw-right-4">
                        <button @click="showDeclinePopup = false" class="tw-p-2 tw-rounded-full hover:tw-bg-white/30 tw-transition-colors">
                            <icon icon="solar:close-circle-bold" class="tw-text-xl tw-text-gray-600" />
                        </button>
                    </div>
                    
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-p-2 tw-bg-white/80 tw-rounded-xl">
                            <icon icon="solar:close-circle-bold" class="tw-text-2xl tw-text-rose-500" />
                        </div>
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
                            {{ $t('common.decline-connection-request') }}
                        </h3>
                    </div>
                </div>

                <!-- Content -->
                <div class="tw-p-6">
                    <p class="tw-text-gray-600">
                        {{ $t('messages.confirm-decline-connection-request') }}
                    </p>
                </div>

                <!-- Footer -->
                <div class="tw-px-6 tw-pb-6 tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <a-button 
                        intent="white" 
                        @click="showDeclinePopup = false"
                    >
                        {{ $t('buttons.no-decline') }}
                    </a-button>
                    <a-button 
                        intent="danger" 
                        @click="confirmDeclineRequest"
                    >
                        {{ $t('buttons.yes-proceed') }}
                    </a-button>
                </div>
            </div>
        </a-popup>
    </div>
</template>

<script setup>
import { getMember } from '@/api/community';
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { roleColorMap } from '@/config/params';
import ConnectionRequestPopup from '@/components/notifications/modals/ConnectionRequestPopup.vue';
import BookMeetingPopup from '@/components/notifications/modals/BookMeetingPopup.vue';
import { cancelRequest, respondToRequest } from '@/api/connections';
import useAlert from '@/composables/useAlert';
import { useI18n } from 'vue-i18n';
import messagingApi from '@/api/messages';

const route = useRoute();
const router = useRouter();
const id = ref(route.params.id);
const { t } = useI18n();

watch(() => route.params.id, (newId) => {
    if (newId !== id.value) {
        id.value = newId;
        fetchMember();
    }
});


const loading = ref(false);
const member = ref(null);
const showConnectionPopup = ref(false);
const showBookMeetingPopup = ref(false);
const showCancelPopup = ref(false);
const showDeclinePopup = ref(false);
const chatLoading = ref(false);

const roleColors = computed(() => {
    if(loading.value) return roleColorMap.default;

    const role = member.value.role?.toLowerCase() || 'default';

    return roleColorMap[role] || roleColorMap.default;
});

const socialMediaLinks = computed(() => {
    if (loading.value || !member.value?.profile) return [];
    
    const socialPlatforms = [
        {
            platform: 'LinkedIn',
            url: member.value.profile.linkedin_url,
            icon: 'ri:linkedin-fill'
        },
        {
            platform: 'Instagram', 
            url: member.value.profile.instagram_url,
            icon: 'proicons:instagram'
        },
        {
            platform: 'Twitter',
            url: member.value.profile.twitter_url,
            icon: 'hugeicons:new-twitter'
        },
        {
            platform: 'Facebook',
            url: member.value.profile.facebook_url,
            icon: 'ri:facebook-fill'
        },
        {
            platform: 'YouTube',
            url: member.value.profile.youtube_url,
            icon: 'famicons:logo-youtube'
        },
        {
            platform: 'GitHub',
            url: member.value.profile.github_url,
            icon: 'mdi:github'
        }
    ];
    
    // Filter out platforms without URLs or with empty/invalid URLs
    return socialPlatforms.filter(social => 
        social.url && 
        social.url.trim() !== ''
    );
});

const websiteUrl = computed(() => {
    if (loading.value || !member.value?.profile?.website_url) return null;
    
    const url = member.value.profile.website_url.trim();
    return url !== '' ? url : null;
});

const mutualConnections = computed(() => {
    if (loading.value || !member.value?.connection?.mutual_connections) return [];
    
    return member.value.connection.mutual_connections || [];
});

const mutualConnectionsCount = computed(() => {
    if (loading.value || !member.value?.connection?.mutual_connections_count) return 0;
    
    return member.value.connection.mutual_connections_count || 0;
});

const recommendations = computed(() => {
    if (loading.value || !member.value?.recommendations) return [];
    
    return member.value.recommendations.slice(0, 3) || [];
});

const getRecommendationRoleColors = (role) => {
    const roleKey = role?.toLowerCase() || 'default';
    return roleColorMap[roleKey] || roleColorMap.default;
};


const fetchMember = async () => {
    loading.value = true;
    console.log('Fetching member with ID:', id.value);
    if(!id.value) return;
    
    await getMember(id.value).then((res) => {
        member.value = res.data;
        console.log('Member data received:', res.data);
        console.log('Recommendations:', res.data.recommendations);

    }).catch((err) => {
        console.error('Error fetching member:', err);
    })

    // Small delay to ensure Vue reactivity
    setTimeout(() => {
        loading.value = false;
        console.log('Loading set to false');
    }, 100);
}


fetchMember();

const handleConnectionSent = () => {
    console.log('Connection sent');
};

const handleMeetingCreated = (meetingData) => {
    console.log('Meeting created:', meetingData);
    // Optionally refresh member data or show additional success message
};

const handleCancelRequest = async () => {
    try {
        const connectionId = parseInt(member.value.connection.id);
        if (!connectionId) {
            throw new Error('Invalid connection ID');
        }
        await cancelRequest(connectionId);
        await fetchMember(); // Refresh member data
        useAlert(t('messages.connection-request-cancelled'), 'success');
    } catch (error) {
        console.error('Error canceling connection request:', error);
        useAlert(t('errors.something-wrong-happened'), 'danger');
    }
};

const confirmCancelRequest = async () => {
    await handleCancelRequest();
    showCancelPopup.value = false;
};

const handleAcceptRequest = async () => {
    try {
        const connectionId = parseInt(member.value.connection.id);
        if (!connectionId) {
            throw new Error('Invalid connection ID');
        }
        await respondToRequest(connectionId, 'accept');
        await fetchMember(); // Refresh member data
        useAlert(t('messages.connection-request-accepted'), 'success');
    } catch (error) {
        console.error('Error accepting connection request:', error);
        useAlert(t('errors.something-wrong-happened'), 'danger');
    }
};

const handleDeclineRequest = async () => {
    try {
        const connectionId = parseInt(member.value.connection.id);
        if (!connectionId) {
            throw new Error('Invalid connection ID');
        }
        await respondToRequest(connectionId, 'decline');
        await fetchMember(); // Refresh member data
        useAlert(t('messages.connection-request-declined'), 'success');
    } catch (error) {
        console.error('Error declining connection request:', error);
        useAlert(t('errors.something-wrong-happened'), 'danger');
    }
};

const confirmDeclineRequest = async () => {
    await handleDeclineRequest();
    showDeclinePopup.value = false;
};

const handleStartChat = async () => {
    if (!member.value?.id || chatLoading.value) return;
    
    chatLoading.value = true;
    
    try {
        // Get the direct conversation with this user
        const response = await messagingApi.conversations.getDirect(member.value.id);
        
        if (response.data?.success && response.data?.data?.id) {
            // Navigate to the messages view with the conversation ID
            router.push({ 
                name: 'shared.messages', 
                params: { id: response.data.data.id } 
            });
        } else {
            throw new Error('Failed to get conversation');
        }
    } catch (error) {
        console.error('Error getting conversation:', error);
        useAlert(t('errors.something-wrong-happened'), 'danger');
    } finally {
        chatLoading.value = false;
    }
};
</script>

<style></style>